import {
  AfterViewInit,
  Directive,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { Router } from '@angular/router';
import { Feature } from '@assetaccountant/api/models/feature';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, combineLatest, of, SubscriptionLike } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { FeatureLimitModalComponent } from '../components/feature-limit-modal/feature-limit-modal.component';
import { SubscriptionService } from '../services/subscription.service';
import { isNotNull } from '../utils/filter-nulls';
import { setupModal, TypedNgbModal } from '../utils/typed-modal';
import { Authorizer, AUTHORIZERS } from './authorizer';
import { getRouteChangesWith } from './authorizer.helpers';

@Directive({
  selector: '[authorizeFeature]',
  providers: [
    {
      provide: AUTHORIZERS,
      useExisting: forwardRef(() => AuthorizeFeatureDirective),
      multi: true,
    },
  ],
  standalone: false,
})
export class AuthorizeFeatureDirective implements Authorizer, AfterViewInit, OnChanges, OnDestroy {
  @Input('authorizeFeature') set feature(
    value: Feature | Array<Feature | null | undefined> | null | undefined,
  ) {
    if (Array.isArray(value)) {
      this.features = value.filter(isNotNull);
    } else if (value) {
      this.features = [value];
    } else {
      this.features = [];
    }
  }

  features: Feature[] = [];

  readonly order = 30;

  private input$ = new BehaviorSubject<null>(null);
  private wrappingElement?: HTMLElement;
  private removeWrappingElement?: () => void;

  readonly changes$ = getRouteChangesWith(
    this.router,
    'organisation',
    ({ activeOrganisation: organisation }) => ({ organisationId: organisation.organisationId }),
    this.input$,
  );

  readonly isAuthorizedChanges = this.changes$.pipe(
    switchMap(data => {
      if (data) {
        return this.isEnabledForOrganisation(data.organisationId).pipe(startWith(false));
      }
      return of(false);
    }),
  );

  // Store current state - needs to be delayed until ViewInit
  // so element is attached to parent
  private isActive$ = new BehaviorSubject<boolean>(true);

  private _subscriptions: SubscriptionLike[] = [];

  constructor(
    private subscriptionService: SubscriptionService,
    private router: Router,
    private renderer: Renderer2,
    private elementRef: ElementRef<HTMLElement>,
    @Inject(NgbModal) public ngbModal: TypedNgbModal,
  ) {}

  ngAfterViewInit() {
    this._subscriptions.push(
      this.isActive$.subscribe(isActive => this.toggleActiveInternal(isActive)),
    );
  }

  ngOnChanges() {
    this.input$.next(null);
  }

  ngOnDestroy() {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  toggleActive(isActive: boolean) {
    this.isActive$.next(isActive);
  }

  private hasAuthorizeShowDirective(element: HTMLElement) {
    // Node type 8 is a comment element which is what is inserted when the authorizeShow directive is active
    return element.nodeType !== 8;
  }

  private toggleActiveInternal(isActive: boolean) {
    const targetElement = this.elementRef.nativeElement;

    if (this.hasAuthorizeShowDirective(targetElement)) {
      if (isActive) {
        this.addWrappingElement(targetElement);
      } else {
        this.removeWrappingElement?.();
        this.removeWrappingElement = undefined;
      }
    }
  }

  private addWrappingElement(targetElement: HTMLElement) {
    if (this.features.length === 0) {
      return;
    }

    const parentElement = this.renderer.parentNode(targetElement);
    const labelElement = this.renderer.nextSibling(targetElement);
    if (!this.wrappingElement) {
      let clickUnlisten = () => {};

      // Handle radio button in a button group
      if (
        parentElement.classList.contains('btn-group') &&
        (targetElement as any).type === 'radio' &&
        labelElement instanceof HTMLLabelElement
      ) {
        const wrap = this.renderer.createElement('span');

        // Move classes to wrapper
        labelElement.classList.forEach(className => {
          wrap.classList.add(className);
        });
        // eslint-disable-next-line ize/ize
        wrap.classList.add('authorize-feature-wrapper');
        labelElement.className = '';

        this.renderer.insertBefore(parentElement, wrap, labelElement);
        this.renderer.appendChild(wrap, labelElement);
        this.wrappingElement = wrap;

        this.removeWrappingElement = () => {
          if (this.wrappingElement) {
            clickUnlisten();

            this.renderer.insertBefore(wrap.parentNode, labelElement, wrap);

            // Move classes back to label
            wrap.classList.forEach(className => {
              labelElement.classList.add(className);
            });

            this.renderer.removeChild(wrap.parentNode, wrap);

            this.wrappingElement = undefined;
          }
        };
      } else {
        const wrap = this.renderer.createElement('span');
        this.renderer.insertBefore(parentElement, wrap, targetElement);
        this.renderer.appendChild(wrap, targetElement);
        this.wrappingElement = wrap;

        this.removeWrappingElement = () => {
          if (this.wrappingElement) {
            this.renderer.insertBefore(wrap.parentNode, targetElement, wrap);
            this.renderer.removeChild(wrap.parentNode, wrap);

            this.wrappingElement = undefined;
          }
        };
      }

      this.renderer.setAttribute(
        this.wrappingElement,
        'title',
        `Your subscription does not include this feature. Click to see upgrade options.`,
      );

      clickUnlisten = this.renderer.listen(this.wrappingElement, 'click', (event: Event) => {
        event.stopImmediatePropagation();
        setupModal(this.ngbModal.open(FeatureLimitModalComponent, { size: 'md' }), modal => {
          modal.features = this.features;
        });
      });
    }
  }

  private isEnabledForOrganisation(organisationId: number) {
    if (!organisationId) {
      return of(false);
    }

    if (this.features.length === 0) {
      return of(true);
    }

    return combineLatest(this.features.map(f => this.isEnabledForFeature(organisationId, f))).pipe(
      map(arr => arr.every(x => !!x)),
    );
  }

  private isEnabledForFeature(organisationId: number, feature: Feature) {
    return this.subscriptionService.isFeatureEnabled(organisationId, feature);
  }
}
